import Icons from "./Icons.js";
import { Tooltip } from "flowbite";

/*
 * This component is used on the directory as a shelf that opens below the result listings.
 * and also used on the Platform and Infrastructure detail pages.
 */
export default class DataObjectDependentsTable {
  constructor(resultPanel, index) {
    this.resultPanel = resultPanel;
    this.index = index;
    this.source = resultPanel.getAttribute("data-source");
    this.name = resultPanel.getAttribute("data-name");
    this.urlSegment = resultPanel.getAttribute("data-url-segment");
    this.items = [];
    this.isOpen = true;
    this.resultPanel.classList.add("shelf-open");
    this.icons = new Icons();

    // Create the table container
    this.table = document.createElement("div");
    this.table.classList.add("data-object-dependents-table");
    this.table.setAttribute("data-index", index);
    this.tableInner = null;
    resultPanel.append(this.table);

    // Start closed
    this.heightZero();

    // Immediately animate to loading spinner height
    var timeout = setTimeout(this.heightLoading.bind(this), 50);

    // With a loading spinner
    this.table.innerHTML =
      `
      <div class="w-full flex items-center justify-center h-20">
        ` +
      this.icons.spinner() +
      `
      </div>`;

    // Get the tables's data
    let url = null;
    if (this.isPlatform()) {
      url = "/api/v1/platforms/detail/" + this.urlSegment;
    } else if (this.isInfrastructure()) {
      url = "/api/v1/infrastructures/detail/" + this.urlSegment;
    }
    if (url === null) {
      return;
    }
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    };
    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok " + response.statusText);
        }
        return response.json();
      })
      .then((jsonData) => {
        // Clear the animation to loading spinner height in case it has not triggered yet
        clearTimeout(timeout);
        this.fillShelf(jsonData);
      });
  }

  isPlatform() {
    return this.source === "Platform";
  }

  isInfrastructure() {
    return this.source === "Infrastructure";
  }

  dependantName() {
    if (this.isPlatform()) {
      return "Infrastructure";
    } else if (this.isInfrastructure()) {
      return "Resource";
    }
    return "";
  }

  dependantNamePlural() {
    if (this.isPlatform()) {
      return "Infrastructures";
    } else if (this.isInfrastructure()) {
      return "Resources";
    }
    return "";
  }

  fillShelf(jsonData) {
    this.items = jsonData[this.dependantNamePlural().toLowerCase()];

    // Remove the spinner
    this.table.innerHTML = "";

    // Page
    this.page = 1;
    this.perPage = 10;
    this.pageMax = Math.floor(this.items.length / this.perPage) + 1;

    // Rows inside the table
    this.tableInner = document.createElement("div");
    this.tableInner.classList.add("inner");

    // Start this faded off
    this.tableInner.style.opacity = 0;

    // Summary
    this.summary = document.createElement("div");
    this.summary.classList.add("summary");
    this.tableInner.append(this.summary);
    this.fillSummary();

    // Header
    let header = document.createElement("div");
    header.classList.add("header");
    let html = "";
    html += '<div class="left">' + this.dependantName() + " Name</div>";
    html += '<div class="right">' + this.dependantName() + " Description</div>";
    header.innerHTML = html;
    this.tableInner.append(header);

    // Rows
    this.rows = document.createElement("div");
    this.rows.classList.add("rows");
    this.tableInner.append(this.rows);
    this.fillRows();

    // Pagination
    this.paginationItems = [];
    if (this.items.length > this.perPage) {
      let pagination = document.createElement("div");
      pagination.classList.add("pagination");
      this.tableInner.append(pagination);

      // Prev
      this.prev = document.createElement("button");
      this.prev.classList.add("previous");
      this.prev.innerHTML = "Previous";
      this.prev.addEventListener("click", this.prevPage.bind(this));
      pagination.append(this.prev);

      // Numbers
      for (let i = 1; i <= this.pageMax; i++) {
        let item = document.createElement("button");
        item.classList.add("item");
        if (this.page === i) {
          item.classList.add("active");
        }
        item.innerHTML = i;
        item.addEventListener("click", this.selectPage.bind(this, i));
        this.paginationItems.push(item);
        pagination.append(item);
      }

      // Next
      this.next = document.createElement("button");
      this.next.classList.add("next");
      this.next.innerHTML = "Next";
      this.next.addEventListener("click", this.nextPage.bind(this));
      pagination.append(this.next);
    }
    this.table.append(this.tableInner);
    this.selectPage(1, null);

    // Animate to full height, and fade on.
    // Delay because things need a second to load
    this.heightFixed();
    setTimeout(this.innerFadeOn.bind(this), 10);
    setTimeout(this.heightFixed.bind(this), 100);
    setTimeout(this.heightFixed.bind(this), 1000);

    // On resize
    window.addEventListener("resize", this.resize.bind(this));
  }

  /*
   * This is called from outside DataObjectDependentsTable to toggle its open / closed state
   */
  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.resultPanel.classList.toggle("shelf-open", this.isOpen);
    if (this.isOpen) {
      this.heightFixed();
    } else {
      this.heightFixed();
      setTimeout(this.heightZero.bind(this), 10);
    }
  }

  resize() {
    //this.heightFixed();
    this.heightAuto();
  }

  heightZero() {
    this.table.style.height = "0px";
  }

  heightLoading() {
    this.table.style.height = "80px";
  }

  heightFixed() {
    if (this.tableInner) {
      // 8px for the shadow below the box making a difference between px and auto height.
      this.table.style.height = this.tableInner.getBoundingClientRect().height + 8 + "px";
    }
  }

  heightAuto() {
    this.table.style.height = "auto";
  }

  innerFadeOn() {
    if (this.tableInner) {
      this.tableInner.style.opacity = 1;
    }
  }

  prevPage(event) {
    this.selectPage(this.page - 1, event);
  }

  nextPage(event) {
    this.selectPage(this.page + 1, event);
  }

  selectPage(n, event) {
    if (event) {
      event.preventDefault();
    }
    this.page = n;
    if (this.page < 1) {
      this.page = 1;
    }
    if (this.page > this.pageMax) {
      this.page = this.pageMax;
    }
    // If theres one page, don't need pagination
    // If theres more than 9 pages, need to hide some of the pagination buttons
    if (this.paginationItems.length > 0) {
      this.prev.classList.toggle("disabled", this.page === 1);
      this.next.classList.toggle("disabled", this.page === this.pageMax);
      for (let i = 0; i < this.paginationItems.length; i++) {
        let item = this.paginationItems[i];
        item.classList.toggle("active", i + 1 === this.page);
        if (this.paginationItems.length > 9) {
          item.classList.toggle("hide", Math.abs(i + 1 - this.page) >= 3);
        }
      }
    }
    this.fillSummary();
    this.fillRows();
  }

  raw2xml(unsafe) {
    return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  fillSummary() {
    if (this.items.length === 1) {
      this.summary.innerHTML = "Showing 1 " + this.dependantName() + " within " + this.raw2xml(this.name);
    } else if (this.items.length <= this.perPage) {
      this.summary.innerHTML = "Showing " + this.items.length + " " + this.dependantNamePlural() + " within " + this.raw2xml(this.name);
    } else {
      let from = Math.min((this.page - 1) * this.perPage + 1, this.items.length);
      let to = Math.min(this.page * this.perPage, this.items.length);
      this.summary.innerHTML =
        "Showing " +
        from +
        " to " +
        to +
        " of " +
        this.items.length +
        " " +
        this.dependantNamePlural() +
        " within " +
        this.raw2xml(this.name);
    }
  }

  fillRows() {
    this.rows.innerHTML = "";
    for (let i = (this.page - 1) * this.perPage; i < Math.min(this.page * this.perPage, this.items.length); i++) {
      this.rows.append(this.fillRow(this.items[i]));
    }
  }

  fillRow(item) {
    // The row
    let row = document.createElement("div");
    row.classList.add("row");

    // The row inner
    let rowInner = document.createElement("div");
    rowInner.classList.add("row-inner");
    row.append(rowInner);

    // The row inner inner
    let rowInnerInner = document.createElement("div");
    rowInnerInner.classList.add("row-inner-inner");
    rowInner.append(rowInnerInner);

    //////////////////////////////////////////////////
    // Left Column
    let leftColumn = document.createElement("div");
    leftColumn.classList.add("left");
    rowInnerInner.append(leftColumn);

    // Left top
    let leftTop = document.createElement("div");
    leftTop.classList.add("top");
    leftColumn.append(leftTop);

    // Accordion arrow
    let arrow = document.createElement("div");
    arrow.classList.add("arrow");
    arrow.innerHTML = this.icons.accordionArrow();
    leftTop.append(arrow);

    // Little Image
    let littleImage = document.createElement("div");
    littleImage.classList.add("image");
    littleImage.classList.add("small");
    if (item.image !== null) {
      littleImage.innerHTML = '<img src="' + item.image + '" />';
    } else {
      littleImage.innerHTML = '<img src="/static/images/placeholder.jpg" />';
    }
    leftTop.append(littleImage);

    // Name Link
    let nameLink = document.createElement("button");
    nameLink.title = "View " + this.dependantName() + " detail";
    nameLink.type = "button";
    nameLink.classList.add("name-link");
    if (this.isPlatform()) {
      nameLink.innerHTML = this.icons.infrastructure();
    } else if (this.isInfrastructure()) {
      nameLink.innerHTML = this.icons.resource();
    }
    nameLink.innerHTML += "<span>" + item.name + "</span>";
    leftTop.append(nameLink);

    // Big Image
    let bigImage = document.createElement("div");
    bigImage.classList.add("image");
    bigImage.classList.add("large");
    let bigImageImg = document.createElement("img");
    if (item.image !== null) {
      bigImageImg.src = item.image;
    } else {
      bigImageImg.src = "/static/images/placeholder.jpg";
    }
    var tempThis = this;
    bigImageImg.addEventListener("load", function () {
      tempThis.resizeRowAccordion(row, rowInnerInner);
      tempThis.heightAuto();
    });
    bigImage.append(bigImageImg);
    leftColumn.append(bigImage);

    // Buttons
    let buttons = document.createElement("div");
    buttons.classList.add("buttons");
    if (this.isPlatform()) {
      buttons.innerHTML += `<a href="${item.link}" class="button">View Infrastructure</a>`;
      buttons.innerHTML += `<a href="${item.contactLink}" class="button hollow">Enquire</a>`;
    } else {
      buttons.innerHTML += `<a href="${item.contactLink}" class="button">Enquire</a>`;
    }
    leftColumn.append(buttons);

    //////////////////////////////////////////////////
    // Right Column
    let rightColumn = document.createElement("div");
    rightColumn.classList.add("right");
    rowInnerInner.append(rightColumn);

    // Descriptions
    let html = "";
    if (item.description) {
      html += `<div class="description">${this.unescapeHtml(item.description)}</div>`;
    }
    if (item.standards) {
      html += `<div class="description"><span>Standards: </span>${item.standards}</div>`;
    }
    if (html === "") {
      html += `<div class="description">&nbsp;</div>`;
    }
    rightColumn.innerHTML = html;

    // Key measurements
    if (item.keyMeasurements && item.keyMeasurements.length > 0) {
      let keyMeasurements = document.createElement("div");
      keyMeasurements.classList.add("two-column-table-rounded");
      keyMeasurements.innerHTML += `<div class="cell-12 cell-header-1">Key Measurements</div>`;
      var showNameAndDescription = false;
      for (const keyMeasurement of item.keyMeasurements) {
        if (keyMeasurement.name && keyMeasurement.description) {
          showNameAndDescription = true;
        }
      }
      if (showNameAndDescription) {
        keyMeasurements.innerHTML += `<div class="cell-s-4 cell-header-2">Name</div>`;
        keyMeasurements.innerHTML += `<div class="cell-e-8 cell-header-2">Description</div>`;
      }
      for (const keyMeasurement of item.keyMeasurements) {
        if (keyMeasurement.name && keyMeasurement.description) {
          keyMeasurements.innerHTML += `<div class="cell-s-4" title="Resource Key Measurement Name">${keyMeasurement.name}</div>`;
          keyMeasurements.innerHTML += `<div class="cell-e-8" title="Resource Key Measurement Description">${keyMeasurement.description}</div>`;
        } else if (keyMeasurement.name) {
          keyMeasurements.innerHTML += `<div class="cell-12" title="Resource Key Measurement">${keyMeasurement.name}</div>`;
        } else {
          keyMeasurements.innerHTML += `<div class="cell-12" title="Resource Key Measurement">${keyMeasurement.description}</div>`;
        }
      }
      rightColumn.append(keyMeasurements);
    }

    // Blocks
    if (
      item.region ||
      item.website ||
      item.count ||
      item.AccessRequirementsInternal ||
      item.AccessRequirementsCommercial ||
      item.AccessRequirementsOpen
    ) {
      let blocks = document.createElement("div");
      blocks.classList.add("blocks");

      if (item.region) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.innerHTML = `
          <div>Region</div>
          <div>${item.region}</div>
        `;
        blocks.append(block);
      }

      if (item.website) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.innerHTML = `
          <div>Website</div>
          <div><a href="${item.website}">${item.website}</a></div>
        `;
        blocks.append(block);
      }

      if (item.count) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.innerHTML = `
          <div>Quantity</div>
          <div>${item.count}</div>
        `;
        blocks.append(block);
      }

      if (item.AccessRequirementsInternal) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.classList.add("w-full");
        block.innerHTML = `
          <div>Internal Access</div>
          <div>${this.icons.infoCircle()} This item for internal users only</div>
        `;
        blocks.append(block);
      }

      if (item.AccessRequirementsCommercial) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.classList.add("w-full");
        block.type = "button";
        block.innerHTML = `
          <div>Commercial Access</div>
          <div>${this.icons.infoCircle()} This item is accessible as a service</div>
        `;
        blocks.append(block);
      }

      if (item.AccessRequirementsOpen) {
        let block = document.createElement("div");
        block.classList.add("block");
        block.classList.add("w-full");
        block.type = "button";
        block.innerHTML = `
          <div>Open Access</div>
          <div>${this.icons.infoCircle()} This item is open to external users</div>
        `;
        blocks.append(block);
      }

      rightColumn.append(blocks);
    }

    //////////////////////////////////////////////////
    window.addEventListener("resize", this.resizeRowAccordion.bind(this, row, rowInnerInner));
    arrow.addEventListener("click", this.toggleRowAccordion.bind(this, row, rowInnerInner));
    nameLink.addEventListener("click", this.toggleRowAccordion.bind(this, row, rowInnerInner));
    if (window.location.hash !== "" && window.location.hash !== "#" && window.location.hash === "#" + item.urlSegment) {
      row.classList.add("open");
      this.resizeRowAccordion(row, rowInnerInner);
      this.heightAuto();
      if (window.detailPageTabChangeTo) {
        if (this.isPlatform()) {
          window.detailPageTabChangeTo("#infrastructure");
        }
        if (this.isInfrastructure()) {
          window.detailPageTabChangeTo("#resources");
        }
      }
    }

    return row;
  }

  toggleRowAccordion(row, rowInnerInner, event) {
    event.preventDefault();
    row.classList.toggle("open");
    this.resizeRowAccordion(row, rowInnerInner);
    this.heightAuto();
  }

  resizeRowAccordion(row, rowInnerInner) {
    if (row.classList.contains("open")) {
      if (rowInnerInner.getBoundingClientRect().height === 0) {
        setTimeout(this.resizeRowAccordion.bind(this, row, rowInnerInner), 100);
      } else {
        row.style.height = rowInnerInner.getBoundingClientRect().height + "px";
      }
    } else {
      row.style.height = "50px";
    }
  }

  unescapeHtml(escapedString) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = escapedString;
    return tempDiv.textContent || tempDiv.innerText || "";
  }
}
